import { createAction } from "redux-actions";
import { INTERACTION_MODE } from 'pxc-editor-material-svg-rendering/constants';
import Section from "pxc-editor-core/entities/Section";
import { getAllLayers, getShieldSectionsArray, getPageObjects, copySectionToShield } from 'pxc-editor-core/helpers/schema';
import { safeObjCopy } from "pxc-editor-core/helpers/common";
import {
  ADD_PAGE,
  DELETE_PAGE,
  DELETE_PAGE_CONTENT,
  OVERRIDE_PAGE,
  OVERRIDE_PAGES,
  CLEAR_PAGES_SELECTION,
  SET_LAST_SAVED_PAGES,
  ADD_PAGE_ROTATION,
  ADD_PAGE_MIRROR_X,
  ADD_PAGE_MIRROR_Y,
  SWITCH_SELECTED_PAGE,
} from '@/constants';
import { createNewPage } from '@/helpers';
import { addSection, addSectionToMatrixSelection, appendSectionAtShield, changePageChildrenLength, clearShield, handleSectionClick, setSelectedPageFirstShieldSection } from "./main";
import { createSnapshotThunk } from "./undoRedo";
import { paginationSetByPageItem } from "./pagination";
import { editSection } from "./section";

const addPageAction = (payload) => {
  return createAction(ADD_PAGE, () => payload)();
};

export const overridePageAction = (payload) => {
  return createAction(OVERRIDE_PAGE, () => payload)();
};

export const overridePagesAction = (payload) => {
  return createAction(OVERRIDE_PAGES, () => payload)();
};

export const setLastSavedPages = () => (dispatch, getState) => {
  const { core: { pages } } = getState();
  dispatch(createAction(SET_LAST_SAVED_PAGES, () => safeObjCopy(pages))());
};

export const addPage = () => (dispatch, getState) => {
  const { core: { selWizard } } = getState();
  const page = createNewPage();

  dispatch(createSnapshotThunk());
  dispatch(addPageAction({ page }));
  dispatch(paginationSetByPageItem(page.uid, true));

  if (page && selWizard?.application_type === 'CABLE_FLAG') {
    dispatch(createCableFlagSections(page.uid));
  }
};

export const deletePage = (pageUid, isUndoable = false) => (dispatch, getState) => {
  const { core: { pages, pagesOrder } } = getState();

  if (pages[pageUid]) {
    const pageToDeleteIndex = pagesOrder.findIndex(el => el === pageUid);
    const leftPageUid = pagesOrder[pageToDeleteIndex - 1];
    const rightPageUid = pagesOrder[pageToDeleteIndex + 1];
    const leftPage = pages[leftPageUid];
    const rightPage = pages[rightPageUid];
    const newSelectedPage = leftPage || rightPage;

    dispatch(createSnapshotThunk({ isUndoable }));

    dispatch(createAction(DELETE_PAGE, () => pages[pageUid])());

    if (newSelectedPage) {
      dispatch(handlePageClick(newSelectedPage.uid));
      dispatch(paginationSetByPageItem(newSelectedPage.uid, true));
    }
  }
};

export const deleteSelectedPage = (isUndoable = false) => (dispatch, getState) => {
  const { core: { pages, pagesSelection } } = getState();
  let selectedPage = null;
  if (pagesSelection.length) {
    const { page } = getAllLayers(pagesSelection[0], pages);
    selectedPage = page;
  }
  if (selectedPage) {
    dispatch(deletePage(selectedPage.uid, isUndoable));
  }
};

export const deletePageContent = (page, isUndoable = false) => dispatch => {
  dispatch(createSnapshotThunk({ isUndoable }));
  dispatch(createAction(DELETE_PAGE_CONTENT, () => page)());
};

// ISN'T USING NOW
export const clearPagesSelection = () =>
  createAction(CLEAR_PAGES_SELECTION, () => null)();

export const switchSelectedPage = pageUid => (dispatch, getState) => {
  dispatch(createAction(SWITCH_SELECTED_PAGE, () => pageUid)());
  dispatch(changePageChildrenLength(pageUid));

  // TODO: is it necessary?
  /* setTimeout(() => {
    const { core: { cmmeStates, pages, sectionsSelection } } = getState();
    if (!!pages && pages[pageUid] && cmmeStates?.pages
      && cmmeStates?.pages[pageUid]) {
      if (cmmeStates?.pages[pageUid]?.sectionsSelection && Object.keys(cmmeStates?.pages[pageUid]?.sectionsSelection)?.length > 1) {
        Object.keys(cmmeStates?.pages[pageUid]?.sectionsSelection).forEach(secid => {
          if (pages[pageUid].sections[secid] && !sectionsSelection[secid]) {
            dispatch(addSectionToMatrixSelection(pages[pageUid].sections[secid]));
          }
        });
      } else if (cmmeStates?.pages[pageUid]?.selectedSection) {
        dispatch(handleSectionClick({ section: pages[pageUid].sections[cmmeStates?.pages[pageUid]?.selectedSection] }));
      }
    }
  }, 10); */
};

export const setSelectedPage = ({ pageUid, isSectionSelection = true }) => (dispatch, getState) => {
  dispatch(switchSelectedPage(pageUid));
  if (isSectionSelection) {
    dispatch(setSelectedPageFirstShieldSection(pageUid));
  }
};

export const createCableFlagSections = pageUid => (dispatch, getState) => {
  const { core: { selWizard, pages } } = getState();
  let pagesToModify = [];
  if (pageUid?.length) {
    pagesToModify.push(pageUid);
    dispatch(switchSelectedPage(pageUid));
  } else {
    if (pages && Object.values(pages).length > 0) {
      Object.values(pages).forEach(page => {
        if (page?.uid?.length) {
          pagesToModify.push(page.uid);
        }
      });
    }
  }

  if (pagesToModify?.length > 0) {
    pagesToModify.forEach(pageUid => {
      if (selWizard?.application_type === 'CABLE_FLAG' && selWizard.data) {
        const flagMidLength = Math.ceil(selWizard.data.flagMidLength);
        let flagLength = Math.ceil(selWizard.data.flagLength * 10) / 10;
        const wizardPages = selWizard?.data?.pages || {};
        wizardPages[pageUid] = {
          flagLength: flagLength
        };
        const { shields: targetShields } = getPageObjects(pages[pageUid]);
        const shieldsArray = targetShields ? Object.values(targetShields).sort((a, b) => a.primitiveProps.x - b.primitiveProps.x) : [];
        if (shieldsArray?.length === 2) {
          const rsX = flagLength + flagMidLength;
          const rsOffset = rsX - shieldsArray[1].primitiveProps.x;
          Object.values(pages[pageUid].sections).filter(sec => sec.shieldId === shieldsArray[1].id).forEach(sec => {
            pages[pageUid].sections[sec.id].x = sec.x + rsOffset;
          });
          shieldsArray[1].primitiveProps.x = rsX;

          const leftShieldSections = getShieldSectionsArray(shieldsArray[0], pages[pageUid]?.sections);
          if (leftShieldSections.length === 0) {
            dispatch(appendSectionAtShield({ shield: shieldsArray[0] }));
          }
          const rightShieldSections = getShieldSectionsArray(shieldsArray[1], pages[pageUid]?.sections);
          if (rightShieldSections.length === 0) {
            dispatch(appendSectionAtShield({ shield: shieldsArray[1] }));
          }


          const { core: { selWizard, pages: newPages } } = getState();
          const sections = Object.values(newPages[pageUid]?.sections).sort((a, b) => a.x - b.x);
          const angle = selWizard.data.textOrientation === 'horizontal' ? 0 : 270;
          const _slaveAngle = angle + Number(selWizard.data.slaveAngle);
          const slaveAngle = _slaveAngle >= 360 ? _slaveAngle - 360 : _slaveAngle;

          if (newPages[pageUid]?.sections && (!!sections.length && sections.length === 2)) {
            const leftSec = Section.fromDict(sections[0]);
            if (sections[0]) {
              leftSec.setAngle(slaveAngle).setX(leftSec.x);
              if (selWizard?.data?.isFlagLengthAuto || !(!!leftSec.isChanged)) {
                leftSec.setWidth(flagLength);
              }

              const leftSecDict = leftSec.toDict();

              dispatch(editSection(leftSecDict));

              newPages[pageUid].sections[leftSecDict.id] = leftSecDict;
            }
            if (sections[1]) {
              const rightSec = Section.fromDict(sections[1]);

              rightSec.setAngle(angle).setX(leftSec.x + flagLength + flagMidLength);
              if (selWizard?.data?.isFlagLengthAuto || !(!!rightSec.isChanged)) {
                rightSec.setWidth(flagLength);
              }

              const rightSecDict = rightSec.toDict();

              dispatch(editSection(rightSecDict));

              newPages[pageUid].sections[rightSecDict.id] = rightSecDict;
              //checkSyncSlaveShieldBySection only one time per page
              if (shieldsArray[1]) {
                setTimeout(() => {
                  dispatch(checkSyncSlaveShieldByMasterShield(shieldsArray[1]));
                  dispatch(handleSectionClick({ section: Object.values(newPages[pageUid].sections)[1] }));
                }, 0);
              }
            }
          } else if (newPages[pageUid]?.sections && sections.length > 2) {
            leftShieldSections.forEach(_sec => {
              const sec = Section.fromDict(_sec);
              sec.setAngle(slaveAngle);
              const secDict = sec.toDict();

              dispatch(editSection(secDict));

              newPages[pageUid].sections[secDict.id] = secDict;
            });
            rightShieldSections.forEach(_sec => {
              const sec = Section.fromDict(_sec);
              sec.setAngle(angle);
              const secDict = sec.toDict();

              dispatch(editSection(secDict));

              newPages[pageUid].sections[secDict.id] = secDict;

            });
            //checkSyncSlaveShieldBySection only one time per page
            if (shieldsArray[1]) {
              dispatch(checkSyncSlaveShieldByMasterShield(shieldsArray[1]));
            }
          }

        }

        dispatch(changePageChildrenLength(pageUid));
      }
      dispatch(switchSelectedPage(pageUid));
    });
  }
};

export const handlePageClick = pageUid => (dispatch, getState) => {
  const { core: { pagesSelection, mode } } = getState();
  const isSelected = pagesSelection.indexOf(pageUid) >= 0;

  switch (mode) {
    case INTERACTION_MODE.EDIT:
      if (!isSelected) {
        dispatch(setSelectedPage({ pageUid }));
      }
      break;
    default:
      break;
  }
};

export const addPageRotation = (angle) => dispatch => {
  dispatch(createAction(ADD_PAGE_ROTATION, () => angle)());
};

// ISN'T USING NOW
export const addPageMirror = (coord) => (dispatch, getState) => {
  const { core: { pagesSelection, pages } } = getState();
  let act = coord === 'x' ? ADD_PAGE_MIRROR_X : ADD_PAGE_MIRROR_Y;
  if (pagesSelection.length && pages[pagesSelection[0]]) {
    const page = pages[pagesSelection[0]];
    if (page.transforms.angle === 90 || page.transforms.angle === 270) {
      act = coord === 'x' ? ADD_PAGE_MIRROR_Y : ADD_PAGE_MIRROR_X;
    }
  }
  dispatch(createAction(act)());
};

export const overridePages = (pages, pagesOrder, isUndoable = false) => (dispatch, getState) => {
  dispatch(createSnapshotThunk({ isUndoable }));
  dispatch(overridePagesAction({ pages, pagesOrder }));
};

export const copyPageLayout = (sourcePageId, targetPageId) => (dispatch, getState) => {
  const { core: { pages } } = getState();
  const sourcePage = pages[sourcePageId];
  const targetPage = pages[targetPageId];
  const { sections: sourceSections } = getPageObjects(sourcePage);
  const { shields: targetShields } = getPageObjects(targetPage);
  const sourceSectionIds = Object.keys(sourceSections);
  const targetShieldIds = Object.keys(targetShields);

  sourceSectionIds.forEach(sourceSecId => {
    const { shield: sourceShield } = getAllLayers(sourceSecId, getState().core.pages);
    const parsedId = sourceShield.id.split('L');
    const postfixToSearch = parsedId[parsedId.length - 1];
    const targetShieldId = targetShieldIds.find(targetId =>
      targetId.includes(postfixToSearch),
    );
    if (targetShieldId) {
      dispatch(copyShieldLayout(sourceShield.id, targetShieldId));
    }
  });
};

export const checkSyncSlaveShieldBySection = (section) => (dispatch, getState) => {
  const { core: { pages, selWizard } } = getState();
  if (selWizard?.application_type === 'CABLE_FLAG' && selWizard?.data) {
    if (pages && Object.values(pages).length > 0) {
      Object.values(pages).forEach(page => {
        if (page.sections[section.id]) {
          if (selWizard?.data?.isCableFlagSync) {
            for (const layoutId in page.layouts) {
              const layout = page.layouts[layoutId];
              for (const groupId in layout.groups) {
                const group = layout.groups[groupId];
                if (group.shields && Object.keys(group.shields).length === 2) {
                  for (const shieldId in group.shields) {
                    if (shieldId !== section.shieldId) {
                      dispatch(clearShield(shieldId));
                      dispatch(copyShieldLayout(section.shieldId, shieldId));
                      if (selWizard?.data?.isFlagLengthAuto) {
                        setTimeout(() => {
                          dispatch(changePageChildrenLength(page.uid));
                        }, 0);
                      }
                      break;
                    }
                  }
                }
              }
            }
          } else {
            if (selWizard?.data?.isFlagLengthAuto) {
              setTimeout(() => {
                dispatch(changePageChildrenLength(page.uid));
              }, 0);
            }
          }
        }
      });
    }
  }
}

export const checkSyncSlaveShieldByMasterShield = (masterShield) => (dispatch, getState) => {
  const { core: { pages, selWizard } } = getState();
  if (selWizard?.application_type === 'CABLE_FLAG' && selWizard?.data) {
    if (pages && Object.values(pages).length > 0) {
      Object.values(pages).forEach(page => {
        const pageUid = page.uid;
        const { shields: targetShields } = getPageObjects(pages[pageUid]);
        if (targetShields[masterShield.id]) {
          if (selWizard?.data?.isCableFlagSync) {
            for (const layoutId in page.layouts) {
              const layout = page.layouts[layoutId];
              for (const groupId in layout.groups) {
                const group = layout.groups[groupId];
                if (group.shields && Object.keys(group.shields).length === 2) {
                  for (const shieldId in group.shields) {
                    if (shieldId !== masterShield.id) {
                      dispatch(clearShield(shieldId));
                      dispatch(copyShieldLayout(masterShield.id, shieldId));
                      if (selWizard?.data?.isFlagLengthAuto) {
                        dispatch(changePageChildrenLength(page.uid));
                      }
                      break;
                    }
                  }
                }
              }
            }
          } else {
            if (selWizard?.data?.isFlagLengthAuto) {
              dispatch(changePageChildrenLength(page.uid));
            }
          }
        }
      });
    }
  }
}


export const copyShieldLayout = (sourceShieldId, targetShieldId, selSectionId) => (dispatch, getState) => {
  const { core: { pages, includeOtherContent, selWizard } } = getState();
  const { page, shield: sourceShield } = getAllLayers(sourceShieldId, pages);
  const sourcePageSections = page?.sections || {};
  const { shield: targetShield } = getAllLayers(targetShieldId, pages);

  getShieldSectionsArray(sourceShield, sourcePageSections).forEach((sourceSection, i) => {
    const secCopy = copySectionToShield(
      sourceSection,
      targetShield,
      getState().core.pages,
      selWizard?.data?.slaveAngle > 0
    );

    if (!includeOtherContent && (selSectionId && selSectionId !== secCopy.sourceSectionId)) {
      return;
    }
    dispatch(addSection(secCopy, false));
  });
};
